import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from 'react';

import {
  TourProvider as ReactTourProvider,
  useTour,
  type PopoverContentProps,
  type StepType,
} from '@reactour/tour';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  MAX_AUTO_SHOW,
  useSettingsStore,
  useToastStore,
  useTourGuideStore,
} from '@tankef/core-react';
import { useTranslation } from '@tankef/i18n';
import { Button } from '@tankef/ui';

import { CONTACTS_TOUR_ID, contactsSteps } from './contacts-tour';
import { DASHBOARD_TOUR_ID, dashboardSteps } from './dashboard-tour';
import { DEPOSIT_TOUR_ID, depositSteps } from './deposit-tour';
import { INV_TABLE_TOUR_ID, invTableSteps } from './inv-table-tour';
import { INVESTMENTS_TOUR_ID, investmentsSteps } from './investments-tour';
import { RECEIPT_TOUR_ID, receiptSteps } from './receipt-tour';
import { SAV_DETAIL_TOUR_ID, savDetailSteps } from './sav-detail-tour';
import { SAVINGS_BOX_TOUR_ID, savingsBoxSteps } from './savings-box-tour';
import { TANDA_TURN_TOUR_ID, tandaTurnSteps } from './tanda-turn-tour';
import { TANDAS_TOUR_ID, tandasSteps } from './tandas-tour';
import { TRANSFERS_TOUR_ID, transfersSteps } from './transfers-tour';
import { WALLET_TOUR_ID, walletSteps } from './wallet-tour';
import { useNavContext } from '../app/layouts/DashboardLayout/nav-context';

// ─── Tour Context ─────────────────────────────────────────────────────────────

interface TourContextValue {
  openTour: () => void;
  startTourById: (tourId: string) => void;
  dismissTour: () => void;
  isOpen: boolean;
  viewCount: number;
  tourId: string | null;
}

const TourContext = createContext<TourContextValue>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openTour: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  startTourById: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dismissTour: () => {},
  isOpen: false,
  viewCount: 0,
  tourId: null,
});

export function useTourContext() {
  return useContext(TourContext);
}

// ─── Route → tour mapping ────────────────────────────────────────────────────

function getTourId(pathname: string): string | null {
  if (pathname.startsWith('/dashboard')) return DASHBOARD_TOUR_ID;
  if (pathname.startsWith('/wallet')) return WALLET_TOUR_ID;
  if (pathname.startsWith('/transfer')) return TRANSFERS_TOUR_ID;
  if (pathname.startsWith('/contacts')) return CONTACTS_TOUR_ID;
  if (pathname.startsWith('/investments')) return INVESTMENTS_TOUR_ID;
  if (pathname.startsWith('/savings-box')) return SAVINGS_BOX_TOUR_ID;
  if (pathname.startsWith('/tandas')) return TANDAS_TOUR_ID;
  return null;
}

const TOUR_ROUTES: Record<string, string> = {
  [DASHBOARD_TOUR_ID]: '/dashboard',
  [WALLET_TOUR_ID]: '/wallet',
  [DEPOSIT_TOUR_ID]: '/wallet',
  [RECEIPT_TOUR_ID]: '/transfer',
  [TRANSFERS_TOUR_ID]: '/transfer',
  [CONTACTS_TOUR_ID]: '/contacts',
  [INVESTMENTS_TOUR_ID]: '/investments',
  [INV_TABLE_TOUR_ID]: '/investments',
  [SAVINGS_BOX_TOUR_ID]: '/savings-box',
  [SAV_DETAIL_TOUR_ID]: '/savings-box',
  [TANDAS_TOUR_ID]: '/tandas',
  [TANDA_TURN_TOUR_ID]: '/tandas',
};

export function getTourRoute(tourId: string): string | null {
  return TOUR_ROUTES[tourId] ?? null;
}

function getTourSteps(tourId: string): StepType[] {
  if (tourId === DASHBOARD_TOUR_ID) return dashboardSteps;
  if (tourId === WALLET_TOUR_ID) return walletSteps;
  if (tourId === DEPOSIT_TOUR_ID) return depositSteps;
  if (tourId === RECEIPT_TOUR_ID) return receiptSteps;
  if (tourId === TRANSFERS_TOUR_ID) return transfersSteps;
  if (tourId === CONTACTS_TOUR_ID) return contactsSteps;
  if (tourId === INVESTMENTS_TOUR_ID) return investmentsSteps;
  if (tourId === INV_TABLE_TOUR_ID) return invTableSteps;
  if (tourId === SAVINGS_BOX_TOUR_ID) return savingsBoxSteps;
  if (tourId === SAV_DETAIL_TOUR_ID) return savDetailSteps;
  if (tourId === TANDAS_TOUR_ID) return tandasSteps;
  if (tourId === TANDA_TURN_TOUR_ID) return tandaTurnSteps;
  return [];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const NAV_SELECTOR_RE = /data-tour="nav-/;

function isNavStep(step: StepType): boolean {
  return typeof step.selector === 'string' && NAV_SELECTOR_RE.test(step.selector);
}

// Polls via rAF until the selector exists in the DOM (element mounted).
// Resolves early as soon as found; gives up after timeoutMs (graceful degradation).
function waitForElement(selector: string, timeoutMs = 3000): Promise<void> {
  return new Promise(resolve => {
    if (document.querySelector(selector)) {
      resolve();
      return;
    }
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (document.querySelector(selector) || Date.now() >= deadline) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

// Poll via rAF until the target element is fully in the viewport (left >= 0).
// This handles framer-motion's slide-in animation — getBoundingClientRect returns
// negative left values while the drawer panel is still translating from off-screen.
function waitForElementVisible(selector: string, timeoutMs = 800): Promise<void> {
  return new Promise(resolve => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        const { left, width } = el.getBoundingClientRect();
        if (left >= 0 && width > 0) {
          resolve();
          return;
        }
      }
      if (Date.now() >= deadline) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

function firstSelector(tourId: string): string | null {
  const steps = getTourSteps(tourId);
  const sel = steps[0]?.selector;
  return typeof sel === 'string' ? sel : null;
}

// ─── Tour popover content (full layout control) ───────────────────────────────

function TourPopoverContent({
  setCurrentStep,
  currentStep,
  setIsOpen,
  steps,
}: PopoverContentProps) {
  const { t } = useTranslation('common');
  const tourGuide = useTourGuideStore();
  const settings = useSettingsStore();
  const addToast = useToastStore(s => s.addToast);
  const { openMobileNav, isMobileNavOpen } = useNavContext();
  const location = useLocation();

  const step = steps[currentStep];
  const stepsLength = steps.length;

  if (!step) return null;

  // Navigate to a step, opening mobile nav first if the target is a nav item
  // and the drawer is currently closed (mobile/tablet only, < 1024px).
  const goToStep = (next: number) => {
    const target = steps[next];
    if (!target) return;

    if (isNavStep(target) && window.innerWidth < 1024 && !isMobileNavOpen) {
      openMobileNav();
      // Wait until the element is fully in the viewport (drawer slide-in complete)
      // before react-tour queries getBoundingClientRect — otherwise it captures
      // a mid-animation position and renders the highlight off-screen.
      waitForElementVisible(target.selector as string).then(() => setCurrentStep(next));
    } else {
      setCurrentStep(next);
    }
  };

  const dismiss = () => {
    setIsOpen(false);
    tourGuide.disable();
    settings.updateHelp({ toursEnabled: false });
    addToast({
      type: 'info',
      title: t('tour.dismissed', { defaultValue: 'Tutoriales desactivados' }),
      message: t('tour.dismissedHint', {
        defaultValue: 'Puedes reactivarlos en Configuración → Ayuda',
      }),
      duration: 6000,
    });
  };

  return (
    <>
      {/* Step content */}
      <div style={{ paddingTop: 14, paddingRight: 16, paddingBottom: 8, paddingLeft: 16 }}>
        {typeof step.content !== 'function' ? step.content : null}
      </div>

      {/* Navigation dots — only shown for multi-step tours */}
      {stepsLength > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 5,
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          {Array.from({ length: stepsLength }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Ir al paso ${i + 1}`}
              onClick={() => goToStep(i)}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                border: 'none',
                outline: 'none',
                padding: 0,
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'background-color 0.2s',
                backgroundColor: i === currentStep ? '#29D6B2' : 'rgba(44,69,74,0.18)',
              }}
            />
          ))}
        </div>
      )}

      {/* Full-width divider */}
      <div style={{ height: 1, backgroundColor: 'var(--tkf-line, rgba(44,69,74,0.15))' }} />

      {/* Footer — action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingTop: 8,
          paddingRight: 14,
          paddingBottom: 8,
          paddingLeft: 14,
          gap: 6,
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg px-3 py-1.5 text-xs text-tkf-fg-subtle transition-colors hover:bg-tkf-surface-subtle hover:text-tkf-fg-muted focus:outline-none"
        >
          No mostrar más
        </button>
        {currentStep > 0 && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setCurrentStep(s => s - 1)}
            className="normal-case"
          >
            Atrás
          </Button>
        )}
        <Button
          type="button"
          variant="primary"
          size="xs"
          onClick={() => {
            if (currentStep < stepsLength - 1) {
              goToStep(currentStep + 1);
            } else {
              setIsOpen(false);
              const activeTourId = getTourId(location.pathname);
              if (activeTourId) tourGuide.markCompleted(activeTourId);
            }
          }}
          className="normal-case"
        >
          {currentStep < stepsLength - 1 ? 'Siguiente' : '¡Listo!'}
        </Button>
      </div>
    </>
  );
}

// ─── Inner controller (must be inside ReactTourProvider) ─────────────────────

function TourInner({ children }: { children: ReactNode }) {
  const { isOpen, setIsOpen, setCurrentStep, setSteps } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const tourGuide = useTourGuideStore();
  const settings = useSettingsStore();

  // autoOpenedRef: prevents re-triggering the auto-open on the same route visit
  const autoOpenedRef = useRef(false);
  // pendingTourRef: tour ID to open after a cross-route navigation
  const pendingTourRef = useRef<string | null>(null);
  // suppressAutoOpenRef: blocks the auto-open effect when an explicit tour was requested.
  // Needed because the route-change effect clears pendingTourRef synchronously, so by the
  // time the auto-open effect runs on the same render cycle pendingTourRef is already null.
  const suppressAutoOpenRef = useRef(false);

  const tourId = getTourId(location.pathname);

  // On route change: reset per-visit flags; fire pending explicit tour if any
  useEffect(() => {
    autoOpenedRef.current = false;
    setCurrentStep(0);

    if (pendingTourRef.current) {
      suppressAutoOpenRef.current = true; // keep blocking auto-open for this route visit
      const pending = pendingTourRef.current;
      pendingTourRef.current = null;
      const steps = getTourSteps(pending);
      const sel = firstSelector(pending);

      let cancelled = false;
      void (async () => {
        // Wait until first step's element is actually in the DOM before opening
        if (sel) await waitForElement(sel);
        if (cancelled) return;
        setSteps?.(steps);
        setIsOpen(true);
        tourGuide.incrementViewCount(pending);
      })();
      return () => {
        cancelled = true;
      };
    }

    suppressAutoOpenRef.current = false;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-open on first route visits (skipped when an explicit tour was just triggered)
  useEffect(() => {
    if (!tourId || autoOpenedRef.current || suppressAutoOpenRef.current) return undefined;
    const state = tourGuide.tours[tourId] ?? { viewCount: 0 };
    const canAutoOpen =
      !tourGuide.disabled && settings.help.toursEnabled && state.viewCount < MAX_AUTO_SHOW;

    if (!canAutoOpen) return undefined;

    const sel = firstSelector(tourId);
    let cancelled = false;
    void (async () => {
      // Brief delay so React finishes the route's initial render commit
      await delay(500);
      if (cancelled || suppressAutoOpenRef.current) return;
      // Then poll until the first step's element is actually mounted
      if (sel) await waitForElement(sel, 2000);
      if (cancelled) return;
      autoOpenedRef.current = true;
      setIsOpen(true);
      tourGuide.incrementViewCount(tourId);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  const openTour = useCallback(() => {
    if (!tourId) return;
    setCurrentStep(0);
    setIsOpen(true);
    tourGuide.incrementViewCount(tourId);
  }, [tourId, setIsOpen, setCurrentStep, tourGuide]);

  const startTourById = useCallback(
    (id: string) => {
      const steps = getTourSteps(id);
      const route = getTourRoute(id);
      if (route && !location.pathname.startsWith(route)) {
        // Cross-route: park the tour ID and block auto-open on the incoming route
        pendingTourRef.current = id;
        suppressAutoOpenRef.current = true;
        navigate(route);
      } else {
        // Same route: poll for element then inject steps and open
        const sel = firstSelector(id);
        void (async () => {
          if (sel) await waitForElement(sel);
          setSteps?.(steps);
          setCurrentStep(0);
          setIsOpen(true);
          tourGuide.incrementViewCount(id);
        })();
      }
    },
    [location.pathname, navigate, setIsOpen, setCurrentStep, setSteps, tourGuide]
  );

  const dismissTour = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const viewCount = tourId ? (tourGuide.tours[tourId]?.viewCount ?? 0) : 0;

  return (
    <TourContext.Provider
      value={{ openTour, startTourById, dismissTour, isOpen, viewCount, tourId }}
    >
      {children}
    </TourContext.Provider>
  );
}

// ─── Public wrapper ───────────────────────────────────────────────────────────

interface AppTourControllerProps {
  children: ReactNode;
}

export function AppTourController({ children }: AppTourControllerProps) {
  const location = useLocation();
  const tourId = getTourId(location.pathname);
  const steps = tourId ? getTourSteps(tourId) : [];

  return (
    <ReactTourProvider
      steps={steps}
      padding={{ mask: 8, popover: [8, 8] }}
      ContentComponent={TourPopoverContent}
      styles={{
        maskWrapper: base => ({ ...base, opacity: 0.4, color: '#000' }),
        maskArea: base => ({ ...base, rx: 12 }),
        popover: base => ({
          ...base,
          backgroundColor: 'var(--tkf-surface, #fff)',
          border: '1px solid var(--tkf-line, rgba(44,69,74,0.15))',
          borderRadius: 16,
          padding: 0,
          boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
          maxWidth: 320,
          minWidth: 280,
          overflow: 'hidden',
        }),
      }}
      showCloseButton={false}
      showBadge={false}
    >
      <TourInner>{children}</TourInner>
    </ReactTourProvider>
  );
}
